import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { lastValueFrom, Observable, of } from 'rxjs'
import { catchError, take, tap } from 'rxjs/operators'
import { CommonService } from '@appShared/services/common.service'
import { IEmployeeSearchResult } from '@appShared/interfaces/[Interface-based]/employee-search-result.interface'
import {
   IDividend,
   IPlan,
   IPlanDetail,
   IShareholder,
   IShareholderSummaryInfo
} from '@appShared/interfaces/[Model-based]'
import * as _ from 'lodash'

export enum ShareholderEditSection_ {
   newEmployeeInfo = 1,
   basicInfo,
   planInfo,
   dividendInfo
}

export interface ShareholderEditSectionData {
   shareholderEditSection: ShareholderEditSection_
   shareholder: IShareholder
   refresh: boolean
}

@Injectable({ providedIn: 'root' })
export class AdminService {
   /* https://blogs.msmvps.com/deborahk/build-a-simple-angular-service-to-share-data/ */
   private _shareholderAdminApi = '/api/admin/shareholders';

   private _httpOptions = {}

   // TODO remove
   //fakeEmployees = [
   //  {
   //    objectId: '00000000-1234-0000-0000-000000000000',
   //    firstName: 'Ryan',
   //    lastName: 'Groom',
   //    company: 'SW Financial Services'
   //  } as IEmployeeSearchResult,
   //  {
   //    objectId: '00000000-0000-5678-0000-000000000000',
   //    firstName: 'Amy',
   //    lastName: 'Pigozzi-Groom',
   //    company: 'Southwestern',
   //    department: 'Bookeeping'
   //  } as IEmployeeSearchResult
   //] as IEmployeeSearchResult[];

   constructor(
      private _http: HttpClient,
      private _commonService: CommonService
   ) {
      this._httpOptions = _commonService.httpPostOptions()
   }

   getShareholders(includeRepurchaseOptionExecuted?: boolean): Observable<IShareholderSummaryInfo[]> {
      const url = `${this._shareholderAdminApi}?includeRepurchaseOptionExecuted=${includeRepurchaseOptionExecuted ?? false}`
      return this._http.get<IShareholderSummaryInfo[]>(url)
   }

   async getShareholder(id: number): Promise<IShareholder> {
      const url = `${this._shareholderAdminApi}/${id}`

      const request$ = this._http.get<IShareholder>(url).pipe(take(1))

      return await lastValueFrom<IShareholder>(request$)
   }

   employeeSearch(term: string): Observable<IEmployeeSearchResult[]> {
      if (term) {
         const url = `${this._shareholderAdminApi}/search?term=${term}`

         return this._http.get<IEmployeeSearchResult[]>(url)
         // const termLowerCase = term.toLowerCase();
         // const filteredEmployees: IEmployeeSearchResult[] = this.fakeEmployees.filter(
         //  employee => {
         //    const employeeInfo = `${employee.firstName} ${employee.lastName
         //      }`.toLowerCase();
         //    return employeeInfo.indexOf(termLowerCase) !== -1;
         //  }
         // );
         // return of(filteredEmployees);
      } else {
         return of([])
      }
   }

   async createUpdateShareholder(
      shareholder: IShareholder
   ): Promise<IShareholder> {
      if (!shareholder) {
         return new Promise((resolve, reject) => reject('No Shareholder passed!'))
      }

      const url = shareholder.id
         ? `${this._shareholderAdminApi}/${shareholder.id}`
         : this._shareholderAdminApi
      const options = this._commonService.httpPostOptions()
      let request$: Observable<IShareholder>

      if (shareholder.id) {
         /* update */
         request$ = this._http.put<IShareholder>(url, shareholder, options).pipe(take(1))
      } else {
         /* else create */
         request$ = this._http.post<IShareholder>(url, shareholder, options).pipe(take(1))
      }

      return await lastValueFrom<IShareholder>(request$)
   }

   async getPlans(shareholderId: number): Promise<IPlan[]> {
      const url = `${this._shareholderAdminApi}/${shareholderId}/plans`

      const request$ = this._http.get<IPlan[]>(url).pipe(take(1))

      return await lastValueFrom<IPlan[]>(request$)
   }

   async createUpdatePlan(plan: IPlan): Promise<IShareholder> {
      if (!plan) {
         return new Promise((resolve, reject) => reject('No Plan passed!'))
      }

      const url = `${this._shareholderAdminApi}/${plan.shareholderId}/plans`
      const options = this._commonService.httpPostOptions()
      let request$: Observable<IShareholder>

      if (plan.id) {
         /* update */
         request$ = this._http.put<IShareholder>(url, plan, options).pipe(take(1))
      } else {
         /* else create */
         request$ = this._http.post<IShareholder>(url, plan, options).pipe(take(1))
      }

      return await lastValueFrom<IShareholder>(request$)
   }

   async getPlanByYear(shareholderId: number, year: number): Promise<IPlan> {
      const url = `${this._shareholderAdminApi
         }/${shareholderId}/plans/year/${year}`

      const request$ = this._http.get<IPlan>(url).pipe(take(1))

      return await lastValueFrom<IPlan>(request$)
   }

   async deletePlan(plan: IPlan): Promise<unknown> {
      if (!plan) {
         return new Promise((resolve, reject) => reject('No stock plan passed!'))
      }

      const url = `${this._shareholderAdminApi}/${plan.shareholderId}/plans/${plan.id
         }`

      const request$ = this._http.delete(url, this._httpOptions).pipe(take(1))

      return await lastValueFrom(request$)
   }

   async getPlanDetails(plan: IPlan): Promise<IPlanDetail[]> {
      const url = `${this._shareholderAdminApi}/${plan.shareholderId}/plans/${plan.id
         }/plan-details`

      const request$ = this._http.get<IPlanDetail[]>(url).pipe(take(1))

      return await lastValueFrom<IPlanDetail[]>(request$)
   }

   async getPlanDetailByYear(
      shareholderId: number,
      planId: number,
      year: number
   ): Promise<IPlanDetail> {
      const url = `${this._shareholderAdminApi
         }/${shareholderId}/plans/${planId}/plan-details/year/${year}`

      const request$ = this._http.get<IPlanDetail>(url).pipe(take(1))

      return await lastValueFrom<IPlanDetail>(request$)
   }

   async createUpdatePlanDetail(planDetail: IPlanDetail): Promise<IShareholder> {
      if (!planDetail) {
         return new Promise((resolve, reject) => reject('No Plan Detail passed!'))
      }

      const plan = planDetail.plan
      const url = `${this._shareholderAdminApi}/${plan.shareholderId}/plans/${plan.id
         }/plan-details`
      const options = this._commonService.httpPostOptions()
      let request$: Observable<IShareholder>

      if (planDetail.id) {
         /* update */
         request$ = this._http.put<IShareholder>(url, planDetail, options).pipe(take(1))
      } else {
         /* else create */
         request$ = this._http.post<IShareholder>(url, planDetail, options).pipe(take(1))
      }

      return await lastValueFrom<IShareholder>(request$)
   }

   async deletePlanDetail(planDetail: IPlanDetail): Promise<unknown> {
      if (!planDetail) {
         return new Promise((resolve, reject) =>
            reject('No stock plan detail passed!')
         )
      }

      const plan = planDetail.plan
      const url = `${this._shareholderAdminApi}/${plan.shareholderId}/plans/${planDetail.planId
         }/plan-details/${planDetail.id}`

      const request$ = this._http.delete(url, this._httpOptions).pipe(take(1))

      return await lastValueFrom(request$)
   }

   async getDividends(shareholderId: number): Promise<IDividend[]> {
      const url = `${this._shareholderAdminApi}/${shareholderId}/dividends`

      const request$ = this._http.get<IDividend[]>(url).pipe(take(1))

      return await lastValueFrom<IDividend[]>(request$)
   }

   async getDividendByYear(
      shareholderId: number,
      year: number
   ): Promise<IDividend> {
      const url = `${this._shareholderAdminApi
         }/${shareholderId}/dividends/year/${year}`

      const request$ = this._http.get<IDividend>(url).pipe(take(1))

      return await lastValueFrom<IDividend>(request$)
   }

   async createUpdateDividend(dividend: IDividend): Promise<IShareholder> {
      if (!dividend) {
         return new Promise((resolve, reject) => reject('No Plan passed!'))
      }

      const url = `${this._shareholderAdminApi}/${dividend.shareholderId
         }/dividends`
      const options = this._commonService.httpPostOptions()
      let request$: Observable<IShareholder>

      if (dividend.id) {
         /* update */
         request$ = this._http.put<IShareholder>(url, dividend, options).pipe(take(1))
      } else {
         /* else create */
         request$ = this._http.post<IShareholder>(url, dividend, options).pipe(take(1))
      }

      return await lastValueFrom<IShareholder>(request$)
   }

   async deleteDividend(dividend: IDividend): Promise<unknown> {
      if (!dividend) {
         return new Promise((resolve, reject) => reject('No dividend passed!'))
      }

      const url = `${this._shareholderAdminApi}/${dividend.shareholderId
         }/dividends/${dividend.id}`

      const request$ = this._http.delete(url, this._httpOptions).pipe(take(1))

      return await lastValueFrom(request$)
   }
}
